import { supabase } from "@/integrations/supabase/client";
import { handleServiceError } from "../utils/errorHandling";
import { SignInResponse, SignUpResponse } from "@/types/auth";
import { LEGAL_VERSIONS, LegalAgreementAcceptance } from "@/constants/legalVersions";
import { getClientInfo } from "@/utils/clientInfo";
import { analytics } from "@/lib/analytics";
import { familyInviteService } from "@/services/family";
import { toast } from "sonner";

/**
 * Service for Apple Sign-In authentication (Web OAuth only)
 */
export const authAppleService = {
  /**
   * Sign in or sign up with Apple (Web OAuth flow)
   */
  signInWithApple: async (inviteCode?: string): Promise<SignInResponse | SignUpResponse> => {
    console.log('🍎 Apple Web Sign-In initiated:', { has_invite_code: !!inviteCode });
    
    try {
      // Track Apple Sign-In attempt
      analytics.track('page_viewed', {
        feature: 'apple_signin_web_initiated',
        has_invite_code: !!inviteCode,
        platform: 'web'
      });

      // Use Supabase OAuth flow for web
      console.log('🍎 Requesting Apple OAuth via Supabase...');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/`,
          scopes: 'email name'
        }
      });

      if (error) {
        console.error('🍎 Apple OAuth error:', error);
        throw error;
      }

      // For OAuth flow, the actual authentication happens via redirect
      // so we return success immediately - the redirect will handle the rest
      console.log('🍎 Apple OAuth initiated successfully');
      
      // Store invite code for later processing after redirect
      if (inviteCode) {
        sessionStorage.setItem('apple_invite_code', inviteCode);
      }

      // Track successful OAuth initiation
      analytics.track('page_viewed', { 
        feature: 'apple_oauth_initiated',
        has_invite_code: !!inviteCode 
      });

      return { session: null, error: null };
      
    } catch (error: any) {
      console.error("Apple Web Sign-In error:", error);
      analytics.trackAuthError('apple_signin_web', error instanceof Error ? error.message : 'Unknown error');
      handleServiceError(error, "Failed to sign in with Apple");
      return { session: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  /**
   * Alias for signInWithApple (web uses same flow)
   */
  signInWithAppleWeb: async (inviteCode?: string): Promise<SignInResponse | SignUpResponse> => {
    return authAppleService.signInWithApple(inviteCode);
  },

  /**
   * Handle family invite for Apple users
   */
  handleFamilyInvite: async (userId: string, inviteCode: string, flow: 'signin' | 'signup') => {
    try {
      const result = await familyInviteService.joinFamilyByInviteCode(userId, inviteCode);
      
      if (result.success) {
        analytics.track('family_joined_during_signup', {
          invite_code: inviteCode,
          family_id: result.familyId,
          auth_provider: 'apple',
          flow: flow
        });
        toast.success(`Successfully ${flow === 'signin' ? 'signed in' : 'registered'} and joined family!`);
      } else {
        toast.error(`${flow === 'signin' ? 'Sign in' : 'Registration'} successful, but failed to join family: ${result.message}`);
      }
    } catch (joinError) {
      console.error("Error joining family:", joinError);
      analytics.trackError(joinError as Error, 'family_join_during_signup');
      toast.error(`${flow === 'signin' ? 'Sign in' : 'Registration'} successful, but failed to join family. You can join later using the invite code.`);
    }
  },

  /**
   * Send welcome email in background
   */
  sendWelcomeEmailBackground: (firstName: string, lastName: string, email: string) => {
    setTimeout(async () => {
      try {
        console.log('Sending welcome email in background for Apple user...');
        const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
          body: { firstName, lastName, email },
        });
        
        if (emailError) {
          console.error('Welcome email failed:', emailError);
        } else {
          console.log('Welcome email sent successfully');
        }
      } catch (emailError) {
        console.error('Welcome email error:', emailError);
      }
    }, 0);
  },

  /**
   * Create legal agreement acceptance data
   */
  createLegalAgreementAcceptance: (): LegalAgreementAcceptance => {
    const clientInfo = getClientInfo();
    
    return {
      timestamp: new Date().toISOString(),
      terms_version: LEGAL_VERSIONS.TERMS_OF_SERVICE,
      privacy_version: LEGAL_VERSIONS.PRIVACY_POLICY,
      ip_address: clientInfo.ipAddress,
      user_agent: clientInfo.userAgent,
    };
  }
};
